import { useState } from 'react';

import { useCollapsibleSection } from 'shared/hooks/collapsibleSections';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

import { useCreateMondaySuccessiveBoardMutation } from '../services/projects.api';
import { MondayIntegration } from '../types/integrations';

const MONDAY_BOARD_CAPACITY = 10000;

interface Props {
  data: MondayIntegration;
  projectId: number;
}

const getCapacityColor = (capacity: number): string => {
  if (capacity >= 0.9) return 'bg-error';
  if (capacity >= 0.7) return 'bg-warning';
  return 'bg-success';
};

const MondaySection = ({ data, projectId }: Props) => {
  const sectionProps = useCollapsibleSection('monday');
  const { connected, formRequestUrl, boards } = data;
  const board = boards[0] ?? null;
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [createSuccessiveBoard, { isLoading }] = useCreateMondaySuccessiveBoardMutation();

  const handleCreateSuccessive = async () => {
    setSuccessMessage(null);
    setErrorMessage(null);
    try {
      const result = await createSuccessiveBoard(projectId).unwrap();
      setSuccessMessage(`Board "${result.boardName}" creado exitosamente.`);
    } catch {
      setErrorMessage('Error al crear el board. Intenta de nuevo.');
    }
  };

  return (
    <Accordion
      type="single"
      collapsible
      {...sectionProps}
      className="bg-bg-surface border border-border-subtle rounded-xl"
    >
      <AccordionItem value="content" className="border-none">
        <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-bg-surface-raised rounded-xl data-[state=open]:rounded-b-none">
          <span className="flex items-center gap-3">
            <span className="font-semibold text-text-default">Monday</span>
            <Badge className={connected ? 'bg-success-light text-success' : 'bg-neutral-100 text-text-subtle'}>
              {connected ? 'Conectado' : 'No conectado'}
            </Badge>
          </span>
        </AccordionTrigger>
        <AccordionContent className="px-5 pb-5 border-t border-border-subtle">
          <div className="flex flex-col gap-4 pt-4">
            {formRequestUrl && (
              <div className="flex flex-col gap-1">
                <p className="text-xs font-semibold uppercase tracking-widest text-text-subtle">Form URL (Inicio)</p>
                <a
                  href={formRequestUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-brand-turquoise hover:underline break-all"
                >
                  {formRequestUrl}
                </a>
              </div>
            )}

            {connected && board ? (
              <>
                <div className="flex flex-col gap-1">
                  <p className="text-xs font-semibold uppercase tracking-widest text-text-subtle">Board</p>
                  <div className="flex items-center gap-2">
                    <a
                      href={board.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-text-default hover:text-brand-vivid hover:underline"
                    >
                      {board.boardName ?? board.boardId}
                    </a>
                    <span className="text-xs text-text-subtle">#{board.boardId}</span>
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-muted">
                      {board.itemCount.toLocaleString()} / {MONDAY_BOARD_CAPACITY.toLocaleString()} items
                    </span>
                    <span className="font-semibold text-text-default">{Math.round(board.capacity * 100)}%</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-neutral-100 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${getCapacityColor(board.capacity)}`}
                      style={{ width: `${Math.min(board.capacity * 100, 100)}%` }}
                    />
                  </div>
                </div>

                {board.canCreateSuccessive && (
                  <>
                    <Separator />
                    <div className="flex flex-col gap-2">
                      <p className="text-sm text-text-muted">
                        El board está al {Math.round(board.capacity * 100)}% de capacidad. Puedes crear el próximo
                        board.
                      </p>
                      <Button
                        size="sm"
                        disabled={isLoading}
                        onClick={() => {
                          void handleCreateSuccessive();
                        }}
                        className="self-start"
                      >
                        {isLoading ? 'Creando board...' : 'Crear siguiente board'}
                      </Button>
                      {successMessage && <p className="text-sm text-success">{successMessage}</p>}
                      {errorMessage && <p className="text-sm text-error">{errorMessage}</p>}
                    </div>
                  </>
                )}
              </>
            ) : (
              !connected && (
                <div className="flex flex-col gap-3">
                  <p className="text-sm text-text-muted">No hay board de Monday configurado para este proyecto.</p>
                  <Button size="sm" asChild className="self-start">
                    <a href={`/admin/projects/${projectId}`} target="_blank" rel="noopener noreferrer">
                      Crear board
                    </a>
                  </Button>
                </div>
              )
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default MondaySection;
